/**
 * This is the message editor you see on Step 1 of the New Message modal.
 *
 * It's basically a channel router — depending on which integration the user picked,
 * it shows a completely different editor:
 *
 *   WHATSAPP  →  pick a template, see a live preview, fill in variables
 *   SMS/RCS   →  just a text box with a character counter
 *   EMAIL     →  subject line + rich text body with formatting
 *
 * WhatsApp is by far the most complex path because WhatsApp doesn't let you send
 * free-form text — you have to use pre-approved templates that have placeholders
 * like {{1}} and {{2}}. So this component handles picking the template, showing
 * what it'll look like, and letting the user fill in the variable values.
 *
 * Important: the field arrays (headerFields, bodyFields, footerFields) come in as
 * props from useNewMessageForm. This component doesn't create its own — it just
 * reads them and renders inputs. useNewMessageForm is the single owner.
 */

import { useCallback, useMemo, useState } from 'react';

import { FieldArrayWithId, useFormContext, useWatch } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

import Link from '@/components/atoms/Link/Link';
import { WhatsAppTemplateCombobox } from '@/components/atoms/WhatsAppTemplateCombobox';
import {
  AutocompleteOption,
  FormAutocomplete,
} from '@/components/ui/custom/Autocomplete/Autocomplete';
import { FormDescription, FormField, FormItem, FormLabel } from '@/components/ui/form';
import { CampaignEditor } from '@/modules/campaigns/components/CampaignEditor';
import { CampaignRichEditor } from '@/modules/campaigns/components/CampaignRichEditor';
import { resolveWhatsAppParameters } from '@/modules/campaigns/utils/utils';
import { WHATSAPP_MANAGER_URL } from '@/modules/whatsapp/constants';
import {
  extractVariableNames,
  getComponentsFromTemplate,
  isMediaHeader,
} from '@/modules/whatsapp/utils';
import { LEAD_DATA_VARIABLES, USER_INFO_VARIABLES } from '@/redux/dialogs/helper';
import { WhatsappTemplate } from '@/shared/types/broadcast';
import { IntegrationType } from '@/shared/types/integration';
import {
  EMAIL_BODY_CHARACTER_LIMIT,
  EMAIL_SUBJECT_CHARACTER_LIMIT,
  SMS_CHARACTER_LIMIT,
} from '@/util/validation/patterns';

import { NewMessageFormValues } from '../../hooks/useNewMessageForm';

import './MessageAuthoring.scss';

const CONTENT_LABEL_KEYS: Record<string, string> = {
  [IntegrationType.INFOBIP_SMS]: 'conversation.new_message.sms_content',
  [IntegrationType.INFOBIP_RCS]: 'conversation.new_message.rcs_content',
};

interface MessageAuthoringProps {
  selectedIntegrationType: IntegrationType;
  templates: WhatsappTemplate[];
  isTemplatesLoading: boolean;
  selectedTemplate: WhatsappTemplate | null;
  headerVariablesCount: number;
  bodyVariablesCount: number;
  footerVariablesCount: number;
  headerFields: FieldArrayWithId<NewMessageFormValues, 'headerParameters'>[];
  bodyFields: FieldArrayWithId<NewMessageFormValues, 'bodyParameters'>[];
  footerFields: FieldArrayWithId<NewMessageFormValues, 'footerParameters'>[];
  onTemplateChange: (templateId: string | null) => void;
  onContentChange: (content: string) => void;
  onSubjectChange: (subject: string) => void;
  /** Hides the "Upload an audience..." helper under SMS/RCS/Email content. */
  hideAudienceVariablesHint?: boolean;
}

export const MessageAuthoring = ({
  selectedIntegrationType,
  templates,
  isTemplatesLoading,
  selectedTemplate,
  headerVariablesCount,
  bodyVariablesCount,
  footerVariablesCount,
  headerFields,
  bodyFields,
  footerFields,
  onTemplateChange,
  onContentChange,
  onSubjectChange,
  hideAudienceVariablesHint = false,
}: MessageAuthoringProps) => {
  const { t } = useTranslation();
  const { control, setValue } = useFormContext<NewMessageFormValues>();
  const [templatePopoverOpen, setTemplatePopoverOpen] = useState(false);

  // This builds the dropdown menu you see when you click a variable input (like the
  // {{1}} field). It has options like "Lead Name", "Lead Email", "User First Name" —
  // split into two groups: lead data (contact info from your CRM) and user info
  // (the agent's info). variableOptions is the raw list, and autocompleteOptions
  // just reshapes it into the format the dropdown component needs.
  const variableOptions = useMemo(
    () => [
      ...LEAD_DATA_VARIABLES.map((v) => ({
        value: `{{${v}}}`,
        label: t(`auto_complete.variable_labels.${v}`),
        type: 'lead_data_variable',
      })),
      ...USER_INFO_VARIABLES.map((v) => ({
        value: `{{${v}}}`,
        label: t(`auto_complete.variable_labels.${v}`),
        type: 'user_info_variable',
      })),
    ],
    [t]
  );

  const autocompleteOptions = useMemo(
    (): AutocompleteOption[] =>
      variableOptions.map((opt) => ({
        value: opt.value || '',
        label: opt.label || '',
        category: opt.type ? t(`auto_complete.group_labels.${opt.type}`) : undefined,
      })),
    [variableOptions, t]
  );

  // No-op callback that enables the "Add new" option in the autocomplete dropdown,
  // letting users type custom free-text values. The actual form value gets set by
  // FormAutocomplete's internal Controller — we just need to provide the callback.
  const handleCreateNewVariable = useCallback((_newValue: string) => {}, []);

  // These watch the form's current variable values and feed them into the gray
  // preview card below. They don't render anything themselves — they're just
  // plumbing. When you pick "Lead Name" from a dropdown, these update, and the
  // preview instantly changes from "Hello {{1}}" to "Hello lead_name".
  //
  // We use useWatch (not watch) so only this component re-renders when values
  // change, instead of the entire form tree above us.
  const watchedHeaderParams: { name: string }[] = (
    useWatch({ control, name: 'headerParameters' }) ?? []
  ).map((p) => ({ name: p.name ?? '' }));
  const watchedBodyParams: { name: string }[] = (
    useWatch({ control, name: 'bodyParameters' }) ?? []
  ).map((p) => ({ name: p.name ?? '' }));
  const watchedFooterParams: { name: string }[] = (
    useWatch({ control, name: 'footerParameters' }) ?? []
  ).map((p) => ({ name: p.name ?? '' }));

  type ParameterFieldName = 'headerParameters' | 'bodyParameters' | 'footerParameters';

  // Here's the problem this solves: some templates have {{1}} in both the header
  // AND the body. Without this, you'd see two separate {{1}} inputs — but the user
  // expects one input that fills both places.
  //
  // So we walk through header → body → footer and build a deduplicated list. For
  // each variable, we check: "have I seen this name before?" If not, it gets its
  // own input. If yes, we just record the extra path so we can sync the value later.
  //
  // Say a template has {{1}} in header and body:
  //   Header {{1}} → never seen before → gets an input, bound to "headerParameters.0.name"
  //   Body {{1}}   → already seen!     → "bodyParameters.0.name" saved as a sync target
  //   Result: one input shows up. When the user picks a value, it writes to both.
  const mergedVariables = useMemo(() => {
    const vars: Array<{
      varDisplay: string;
      fieldId: string;
      primaryPath: `${ParameterFieldName}.${number}.name`;
      syncPaths: `${ParameterFieldName}.${number}.name`[];
    }> = [];
    // Tracks which variable names we've already seen — key is "{{1}}", value is its index in vars[]
    const seen = new Map<string, number>();

    /** Process one template component's variables and add them to the list. */
    const addComponent = (
      componentType: 'header' | 'body' | 'footer',
      fields: typeof headerFields,
      fieldName: ParameterFieldName
    ) => {
      if (fields.length === 0) {
        return;
      }
      // If the header is an image/video/document, skip it — those use a file upload, not text
      if (componentType === 'header' && selectedTemplate) {
        const { header } = getComponentsFromTemplate(selectedTemplate);
        if (isMediaHeader(header)) {
          return;
        }
      }

      const variableNames = selectedTemplate
        ? extractVariableNames(selectedTemplate, componentType)
        : [];

      fields.forEach((field, index) => {
        const varDisplay =
          selectedTemplate?.parameter_format === 'NAMED' && index < variableNames.length
            ? `{{${variableNames[index]}}}`
            : `{{${index + 1}}}`;

        const path = `${fieldName}.${index}.name` as const;

        if (seen.has(varDisplay)) {
          // Already seen this one — just remember the path so we can sync it later
          vars[seen.get(varDisplay)!].syncPaths.push(path);
        } else {
          // First time seeing this variable — it gets its own input
          seen.set(varDisplay, vars.length);
          vars.push({ varDisplay, fieldId: field.id, primaryPath: path, syncPaths: [] });
        }
      });
    };

    addComponent('header', headerFields, 'headerParameters');
    addComponent('body', bodyFields, 'bodyParameters');
    addComponent('footer', footerFields, 'footerParameters');

    return vars;
  }, [headerFields, bodyFields, footerFields, selectedTemplate]);

  // ─── WhatsApp channel ───────────────────────────────────────────────
  // This is the most complex path. You see three things stacked top to bottom:
  //   1. A dropdown to pick which template to use
  //   2. A gray preview card that shows the template text with variables filled in live
  //   3. A grid of variable inputs (one per unique variable, built from mergedVariables)
  if (selectedIntegrationType === IntegrationType.WHATSAPP) {
    const hasVariables =
      headerVariablesCount > 0 || bodyVariablesCount > 0 || footerVariablesCount > 0;

    return (
      <FormField
        control={control}
        name="templateId"
        render={({ field, fieldState }) => (
          <FormItem className="flex flex-col gap-4 space-y-0">
            {/* Template picker dropdown */}
            <div className="flex flex-col gap-2">
              <FormLabel>{t('conversation.new_message.templates')}</FormLabel>
              <WhatsAppTemplateCombobox
                templates={templates}
                selectedTemplateId={field.value}
                isLoading={isTemplatesLoading}
                onSelect={onTemplateChange}
                open={templatePopoverOpen}
                onOpenChange={setTemplatePopoverOpen}
                placeholder={t('conversation.new_message.templates_placeholder')}
                popoverClassName="z-1300"
                placeholderClassName="text-foreground-muted"
              />
              {fieldState.error?.message &&
              fieldState.error.message !==
                'conversation.new_message.validation.variables_required' ? (
                <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
              ) : (
                <FormDescription>
                  <Trans
                    i18nKey="broadcasts.select_template"
                    components={{ link1: <Link href={WHATSAPP_MANAGER_URL} external /> }}
                  />
                </FormDescription>
              )}
            </div>

            {/* Once a template is selected, show the preview and variable inputs */}
            {selectedTemplate &&
              (() => {
                // Pull apart the template into its pieces (header, body, footer, buttons)
                // and build the preview. resolveWhatsAppParameters swaps {{1}} with
                // whatever the user picked, so the preview updates as they go.
                const { header, body, footer, buttons } =
                  getComponentsFromTemplate(selectedTemplate);
                const sections: { label: string; text: string }[] = [];
                if (header?.text) {
                  sections.push({
                    label: t('broadcasts.template_header'),
                    text: resolveWhatsAppParameters(header.text, watchedHeaderParams),
                  });
                }
                if (body?.text) {
                  sections.push({
                    label: t('dialog.email.body'),
                    text: resolveWhatsAppParameters(body.text, watchedBodyParams),
                  });
                }
                if (footer?.text) {
                  sections.push({
                    label: t('broadcasts.footer'),
                    text: resolveWhatsAppParameters(footer.text, watchedFooterParams),
                  });
                }
                if (buttons?.length) {
                  sections.push({
                    label: t('dialog.carousel.buttons'),
                    text: buttons.map((b) => b.text).join(', '),
                  });
                }

                return (
                  <>
                    {/* Gray preview box — shows template text with variables filled in */}
                    {sections.length > 0 && (
                      <div className="flex flex-col gap-2 rounded-lg border bg-bg-muted p-4">
                        {sections.map(({ label, text }) => (
                          <div key={label} className="text-sm">
                            <span className="font-semibold">{label}: </span>
                            <span className="whitespace-pre-line">{text}</span>
                          </div>
                        ))}
                      </div>
                    )}

                    {/* Each row here is one unique variable: a label pill ("{{1}}") on the
                        left and a dropdown on the right. The dropdown writes to primaryPath
                        (like "headerParameters.0.name").

                        If this variable shows up in multiple template components (say header
                        AND body), syncPaths won't be empty — and the onChange callback will
                        copy the value to those other paths too. That way when we hit send,
                        every component gets the right value. */}
                    {hasVariables && (
                      <div className="flex flex-col gap-2">
                        <FormLabel>{t('try_it.add_variables', 'Add Variables')}</FormLabel>
                        <div className="grid grid-cols-2 gap-2 pb-0.5">
                          {mergedVariables.map(
                            ({ varDisplay, fieldId, primaryPath, syncPaths }) => (
                              <div key={fieldId} className="flex">
                                <div className="flex h-10 min-w-14 items-center justify-center rounded-l-md border border-r-0 border-stroke bg-bg-muted px-2 text-sm text-foreground-muted">
                                  {varDisplay}
                                </div>
                                <FormAutocomplete<NewMessageFormValues>
                                  control={control}
                                  options={autocompleteOptions}
                                  name={primaryPath}
                                  placeholder={t('broadcasts.select_variable', { 0: varDisplay })}
                                  className="flex-1 [&_button]:rounded-l-none"
                                  onCreateNew={handleCreateNewVariable}
                                  onChange={
                                    syncPaths.length > 0
                                      ? (value) => {
                                          syncPaths.forEach((p) =>
                                            setValue(p, value, { shouldValidate: true })
                                          );
                                        }
                                      : undefined
                                  }
                                />
                              </div>
                            )
                          )}
                        </div>
                        {field.value && fieldState.error?.message && (
                          <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
                        )}
                      </div>
                    )}
                  </>
                );
              })()}
          </FormItem>
        )}
      />
    );
  }

  // ─── SMS / RCS channel ──────────────────────────────────────────────
  // Way simpler than WhatsApp. Just a text box where you type your message.
  // Variables are typed inline (no template, no separate input grid).
  // There's a character counter in the top-right (SMS: 160 chars, RCS: 5000).
  if (
    selectedIntegrationType === IntegrationType.INFOBIP_SMS ||
    selectedIntegrationType === IntegrationType.INFOBIP_RCS
  ) {
    const contentLabel = t(
      CONTENT_LABEL_KEYS[selectedIntegrationType] || 'conversation.new_message.content'
    );
    const charLimit =
      selectedIntegrationType === IntegrationType.INFOBIP_RCS
        ? EMAIL_BODY_CHARACTER_LIMIT
        : SMS_CHARACTER_LIMIT;

    return (
      <FormField
        control={control}
        name="content"
        render={({ field, fieldState }) => (
          <FormItem>
            <div className="flex items-center justify-between">
              <FormLabel>{contentLabel}</FormLabel>
              <div className="text-xs text-foreground-muted">
                {(field.value ?? '').length}/{charLimit}
              </div>
            </div>
            <CampaignEditor
              content={field.value ?? ''}
              onContentChange={onContentChange}
              variableOptions={variableOptions}
              maxLength={charLimit}
            />
            {fieldState.error?.message ? (
              <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
            ) : hideAudienceVariablesHint ? null : (
              <p className="text-xs text-foreground">{t('broadcasts.add_variables_by_typing')}</p>
            )}
          </FormItem>
        )}
      />
    );
  }

  // ─── Email channel ─────────────────────────────────────────────────
  // Two editors stacked on top of each other. The subject is plain text with
  // a 200 char limit. The body is a rich text editor — you can use slash
  // commands to add headings, tables, images, etc. Variables work the same
  // as SMS/RCS (typed inline).
  if (selectedIntegrationType === IntegrationType.EMAIL) {
    return (
      <div className="flex flex-col gap-4">
        <FormField
          control={control}
          name="subject"
          render={({ field, fieldState }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>{t('campaigns.email_subject')}</FormLabel>
                <div className="text-xs text-foreground-muted">
                  {(field.value ?? '').length}/{EMAIL_SUBJECT_CHARACTER_LIMIT}
                </div>
              </div>
              <CampaignEditor
                content={field.value ?? ''}
                onContentChange={onSubjectChange}
                variableOptions={variableOptions}
                className="subject"
                maxLength={EMAIL_SUBJECT_CHARACTER_LIMIT}
              />
              {fieldState.error?.message && (
                <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
              )}
            </FormItem>
          )}
        />
        <FormField
          control={control}
          name="content"
          render={({ field, fieldState }) => (
            <FormItem>
              <div className="flex items-center justify-between">
                <FormLabel>{t('campaigns.email_content')}</FormLabel>
                <div className="text-xs text-foreground-muted">
                  {(field.value ?? '').length}/{EMAIL_BODY_CHARACTER_LIMIT}
                </div>
              </div>
              <CampaignRichEditor
                content={field.value ?? ''}
                onContentChange={onContentChange}
                variableOptions={variableOptions}
                maxLength={EMAIL_BODY_CHARACTER_LIMIT}
                className="dm-editor"
                allowedSlashKeys={[
                  'heading',
                  'heading_2',
                  'heading_3',
                  'heading_4',
                  'paragraph',
                  'numbered_list',
                  'bullet_list',
                  'check_list',
                  'code_block',
                  'table',
                  'image',
                  'video',
                  'audio',
                  'file',
                  'emoji',
                ]}
                allowedBlockTypeKeys={[
                  'heading',
                  'heading_2',
                  'heading_3',
                  'heading_4',
                  'paragraph',
                  'numbered_list',
                  'bullet_list',
                  'check_list',
                ]}
              />
              {fieldState.error?.message ? (
                <p className="text-sm text-warning">{t(fieldState.error.message)}</p>
              ) : hideAudienceVariablesHint ? null : (
                <p className="text-xs text-foreground">{t('broadcasts.add_variables_by_typing')}</p>
              )}
            </FormItem>
          )}
        />
      </div>
    );
  }

  return null;
};
